var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { AbortableNavigateEventListener, ChangeEvent, ChangeEventListener, FocusEventListener, NavigateEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
class DateFieldAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onBlur = new FocusEventListener();
        /** @event */
        this.onFocus = new FocusEventListener();
        /** @event */
        this.onChange = new ChangeEventListener();
        /** @event */
        this.onAfterNavigate = new NavigateEventListener();
        /** @event */
        this.onNavigate = new AbortableNavigateEventListener();
    }
    /**
     * Gets the expression of the apply button.
     *
     * @returns the expression of the apply button.
     */
    getApplyExpression() {
        return this.state.applyExpression;
    }
    /**
     * Sets the expression of the apply button.
     *
     * @param applyExpression - The expression of the apply button.
     */
    setApplyExpression(applyExpression) {
        this.setState({ applyExpression });
    }
    /**
     * Gets the expression of the clear button.
     *
     * @returns the expression of the clear button.
     */
    getClearExpression() {
        return this.state.clearExpression;
    }
    /**
     * Sets the expression of the clear button.
     *
     * @param clearExpression - The expression of the clear button.
     */
    setClearExpression(clearExpression) {
        this.setState({ clearExpression });
    }
    /**
     * Returns if the date field is enabled.
     */
    isEnabled() {
        return this.state.enabled;
    }
    /**
     * Sets if the date field is enabled.
     *
     * @param enabled - If the date field is enabled.
     */
    setEnabled(enabled) {
        this.setState({ enabled });
    }
    /**
     * Gets the expression of the today button.
     *
     * @returns the expression of the today button.
     */
    getTodayExpression() {
        return this.state.todayExpression;
    }
    /**
     * Sets the expression of the today button.
     *
     * @param todayExpression - The expression of the today button.
     */
    setTodayExpression(todayExpression) {
        this.setState({ todayExpression: todayExpression });
    }
    /**
     * Gets the initial date of the field.
     *
     * @returns the initial date of the field.
     */
    getInitialDate() {
        return this.state.initialDate;
    }
    /**
     * Sets the initial date of the field.
     *
     * @param initialDate - The initial date of the field.
     */
    setInitialDate(initialDate) {
        this.setState({ initialDate });
    }
    /**
     * Checks if the field is saving a date interval.
     *
     * @returns a boolean indicating if the field is saving a date interval.
     */
    getInterval() {
        return this.state.interval;
    }
    /**
     * Sets if the field should accept a date interval.
     *
     * @param visible - If the field should accept a date interval.
     */
    setInterval(interval) {
        this.setState({ interval });
    }
    /**
     * Gets the interval selection threshold.
     *
     * @returns the interval selection threshold.
     */
    getIntervalThreshold() {
        return this.state.intervalThreshold;
    }
    /**
     * Sets the interval selection threshold.
     *
     * @param intervalThreshold - The interval selection threshold.
     */
    setIntervalThreshold(intervalThreshold) {
        this.setState({ intervalThreshold });
    }
    /**
     * Gets the value of the field.
     *
     * @returns the value of the field.
     */
    getValue() {
        return this.state.value;
    }
    /**
     * Sets the value of the field.
     *
     * @param value - The value of the field.
     */
    setValue(value) {
        const { value: oldValue } = this.state;
        this.setState({ value });
        this.onChange.fire(new ChangeEvent({ oldValue, value }));
    }
    /**
     * Checks if the field is read only.
     *
     * @returns a boolean indicating if the field is read only.
     */
    isReadOnly() {
        return this.state.readOnly;
    }
    /**
     * Sets if the field should be read only.
     *
     * @param readOnly - If the field should be read only.
     */
    setReadOnly(readOnly) {
        this.setState({ readOnly });
    }
    /**
     * Gets the ranges for date selection.
     *
     * @returns the ranges for date selection.
     */
    getRanges() {
        return this.state.ranges;
    }
    /**
     * Sets the ranges for date selection.
     *
     * @param ranges - The ranges for date selection.
     */
    setRanges(ranges) {
        this.setState({ ranges });
    }
    /**
     * Adds a new range to the field range options.
     *
     * @param startDate - The start date of the range.
     * @param endDate - The end date of the range.
     */
    addRange(startDate, endDate) {
        const ranges = this.getRanges();
        ranges.push({ endDate, startDate });
        this.setRanges(ranges);
    }
    /**
     * Adds one or more ranges to the field range options.
     *
     * @param ranges - The ranges to be added.
     */
    addRanges(ranges) {
        const oldRanges = this.getRanges();
        this.setRanges(oldRanges.concat(ranges));
    }
    /**
     * Clears all the range options of the field.
     */
    clearRanges() {
        this.setRanges([]);
    }
    /**
     * Sets the if the range selection should be enabled.
     *
     * @param rangeState - The range state.
     */
    setRangeState(rangeState) {
        this.setState({ rangeState });
    }
    /**
     * Gets the state of the range selection.
     *
     * @returns the state of the range selection.
     */
    getRangeState() {
        return this.state.rangeState;
    }
    /**
     * Checks if the field value is being struckthrough.
     *
     * @returns a boolean indicating if the field value is being struckthrough.
     */
    isStrikethrough() {
        return Boolean(this.state.strikethrough);
    }
    /**
     * Sets if the field value should be struckthrough.
     *
     * @param strikethrough - If the field value should be struckthrough.
     */
    setStrikethrough(strikethrough) {
        this.setState({ strikethrough });
    }
    /**
     * Gets the configured sensitiveness for the field.
     *
     * @returns the configured sensitiveness for the field.
     */
    getSensitiveData() {
        return this.state.sensitiveData;
    }
    /**
     * Sets if the data of the field should be hidden when in read only mode.
     *
     * @param sensitiveData - How sensitive the data is.
     */
    setSensitiveData(sensitiveData) {
        this.setState({ sensitiveData });
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DateFieldAPI.prototype, "onBlur", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DateFieldAPI.prototype, "onFocus", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DateFieldAPI.prototype, "onChange", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DateFieldAPI.prototype, "onAfterNavigate", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DateFieldAPI.prototype, "onNavigate", void 0);
export { DateFieldAPI, };
//# sourceMappingURL=date-field-api.js.map